import React, { useEffect, useState } from 'react';
import Avatar from 'react-avatar';
import { Play, Square, Clock, Search, X, Wrench, User as UserIcon, Calendar, Timer, Edit3 } from 'lucide-react';

import axios from 'helpers/axios';
import config from 'helpers/config';
import ClockIn from 'models/ClockIn';
import RepairOrder from 'models/RepairOrderModel';
import User from 'models/UserModel';
import moment from 'moment';
import HoverTooltip from 'components/common/HoverTooltip';
import dateFormats from 'helpers/dateFormats';
import CustomerFullName from 'components/common/CustomerFullName';
import getFullVehicleName from 'helpers/vehicleFullNameHelper';

interface Props {
    user: User;

    timeLogs: ClockIn[];
    onChange: () => void;
    activeRepairOrders: RepairOrder[];
}

const UserClockInCard = ({ user, timeLogs = [], onChange, activeRepairOrders = [] }: Props) => {
    const [isClockedIn, setIsClockedIn] = useState(false);
    const [isROSearchExpanded, setIsROSearchExpanded] = useState(false);
    const [activeTimeLog, setActiveTimeLog] = useState<ClockIn>({ userId: user.id, description: '' } as ClockIn);

    const [startTime, setStartTime] = useState<number | null>(null);
    const [timePassed, setTimePassed] = useState(0);

    useEffect(() => {
        const intervalId = setInterval(updateTime, 1000);
        return () => {
            clearInterval(intervalId);
        };
    }, [startTime]);

    useEffect(() => {
        const currentClockedIn = timeLogs.find((log) => log.userId === user.id && !log.clockOutTime);
        if (currentClockedIn) {
            setIsClockedIn(true);
            setActiveTimeLog(currentClockedIn);
            setStartTime(new Date(currentClockedIn.clockInTime).getTime());
        }
    }, [timeLogs]);

    const startClock = () => {
        axios
            .post(`${config.apiUrl}/timeLog/startClockIn`, {
                userId: user.id,
                repairorderId: activeTimeLog?.repairorderId,
                // description: noteContent,
                clockInTime: new Date().getTime(),
            })
            .then((res) => {
                setActiveTimeLog(res.data);
            })
            .catch((_) => console.log(_));
        setStartTime(new Date().getTime());
        onChange();
    };

    const stopClock = () => {
        axios
            .put(`${config.apiUrl}/timeLog/clockOut/${activeTimeLog!.id}`)
            .then((_) => setActiveTimeLog({ userId: user.id, description: '' } as ClockIn))
            .catch((_) => console.log(_))
            .finally(() => onChange());
    };
    const updateTimeLog = (updatedTimeLog?: ClockIn) => {
        const timeLogToUpdate = updatedTimeLog || activeTimeLog;
        axios
            .put(`${config.apiUrl}/timeLog`, {
                ...timeLogToUpdate,
            })
            .then((res) => {
                setActiveTimeLog(res.data);
            })
            .then(() => onChange())
            .catch((_) => console.log(_));
    };

    const updateTime = () => {
        if (startTime) {
            const currentTime = new Date().getTime();
            const elapsedTime = currentTime - startTime;
            setTimePassed(elapsedTime);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-visible hover:shadow-md transition-all duration-200 h-fit">
            {/* Header */}
            <div
                className={`px-6 py-4 border-b ${
                    isClockedIn ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}
            >
                <div className="flex items-center space-x-3">
                    <div className="relative">
                        <Avatar name={`${user.firstName} ${user.lastName}`} round size="48" />
                        {isClockedIn && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white">
                                <div className="w-full h-full bg-green-500 rounded-full animate-pulse"></div>
                            </div>
                        )}
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {user.firstName} {user.lastName}
                        </h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <UserIcon className="h-3 w-3" />
                            <span>{isClockedIn ? 'Active' : 'Inactive'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Timer Display */}
            {isClockedIn && (
                <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-green-50 border-b border-gray-200">
                    <div className="flex items-center justify-center space-x-2 mb-2">
                        <Clock className="h-5 w-5 text-blue-600" />
                        <span className="text-sm font-semibold text-gray-700">Current Session</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2 mb-2">
                        <span className="text-xs text-gray-500">
                            {startTime ? (
                                <span>
                                    Clocked in at{' '}
                                    <HoverTooltip content={moment(startTime).format(dateFormats.USA_DATETIME)}>
                                        <span className="font-semibold">
                                            {/* if more than 24 hours passed, display date time instead of only time*/}
                                            {moment(startTime).isBefore(moment().subtract(1, 'day'))
                                                ? moment(startTime).format(dateFormats.USA_DATE_TIME_LONG)
                                                : moment(startTime).format(dateFormats.USA_TIME)}
                                        </span>
                                    </HoverTooltip>
                                </span>
                            ) : (
                                'Not clocked in'
                            )}
                        </span>
                    </div>
                    <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600 font-mono">
                            <span className="whitespace-nowrap">
                                {' '}
                                {/* dont show days if 0 */}
                                {moment.duration(timePassed).days() > 0 && (
                                    <span>
                                        {moment.duration(timePassed).days()}{' '}
                                        {moment.duration(timePassed).days() === 1 ? 'day' : 'days'}
                                    </span>
                                )}{' '}
                                {moment.utc(timePassed).format('HH:mm:ss')}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Time Statistics */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2 mb-3">
                    <Timer className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-semibold text-gray-700">Time Summary</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-xs font-semibold text-blue-600 mb-1">Today</div>
                        <div className="text-sm font-bold text-blue-900">
                            {user.totalTime
                                ? `${user.totalTime.totalTimeToday.hours}h ${user.totalTime.totalTimeToday.minutes}m`
                                : '0h 0m'}
                        </div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="text-xs font-semibold text-purple-600 mb-1">This Week</div>
                        <div className="text-sm font-bold text-purple-900">
                            {user.totalTime
                                ? `${user.totalTime.totalTimePastWeek.hours}h ${user.totalTime.totalTimePastWeek.minutes}m`
                                : '0h 0m'}
                        </div>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="text-xs font-semibold text-orange-600 mb-1">This Month</div>
                        <div className="text-sm font-bold text-orange-900">
                            {user.totalTime
                                ? `${user.totalTime.totalTimePastMonth.hours}h ${user.totalTime.totalTimePastMonth.minutes}m`
                                : '0h 0m'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Notes Section */}
            {isClockedIn && (
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2 mb-3">
                        <Edit3 className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-semibold text-gray-700">Session Notes</span>
                    </div>
                    <textarea
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                        placeholder="Add notes about your current work session..."
                        rows={3}
                        value={activeTimeLog?.description || ''}
                        onChange={(e) =>
                            setActiveTimeLog({ ...(activeTimeLog as ClockIn), description: e.target.value })
                        }
                        onBlur={() => {
                            updateTimeLog(activeTimeLog);
                        }}
                    />
                </div>
            )}

            {/* Activity Section */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2 mb-3">
                    <Wrench className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-semibold text-gray-700">Current Activity</span>
                </div>

                {activeTimeLog?.repairorder && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                    <Wrench className="h-3 w-3 text-blue-600" />
                                    <span className="text-sm font-semibold text-blue-900">
                                        #{activeTimeLog.repairorder.repairOrderNumber}
                                    </span>
                                </div>
                                <p className="text-xs text-blue-700 line-clamp-2">{activeTimeLog.repairorder.title}</p>
                            </div>{' '}
                            <button
                                onClick={() => {
                                    const updatedTimeLog = {
                                        ...activeTimeLog,
                                        repairorderId: null,
                                        repairorder: null,
                                    } as ClockIn;
                                    setActiveTimeLog(updatedTimeLog);
                                    updateTimeLog(updatedTimeLog);
                                }}
                                className="p-1 text-blue-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Remove repair order"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    </div>
                )}

                {!!!activeTimeLog.repairorderId && (
                    <div className="relative">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                placeholder="Search repair orders..."
                                autoComplete="off"
                                onFocus={() => setIsROSearchExpanded(true)}
                                onBlur={() => {
                                    setTimeout(() => {
                                        setIsROSearchExpanded(false);
                                    }, 300);
                                }}
                            />
                        </div>

                        {isROSearchExpanded && (
                            <div className="absolute z-99999 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-scroll">
                                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                                    <span className="text-xs font-semibold text-gray-600">
                                        {activeRepairOrders?.length || 0} active repair orders available
                                    </span>
                                </div>
                                <div className="max-h-64">
                                    {activeRepairOrders?.map((RO: RepairOrder, index) => (
                                        <button
                                            key={index}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();

                                                const updatedTimeLog = {
                                                    ...activeTimeLog,
                                                    repairorderId: RO.id,
                                                    repairorder: RO,
                                                };
                                                setActiveTimeLog(updatedTimeLog);
                                                updateTimeLog(updatedTimeLog);
                                                setIsROSearchExpanded(false);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    {/* RO Number and Status */}
                                                    <div className="flex items-center space-x-2 mb-2">
                                                        <span className="text-sm font-bold text-green-600 group-hover:text-green-800">
                                                            #{RO.repairOrderNumber}
                                                        </span>
                                                        {RO.title && (
                                                            <span className="text-sm font-semibold text-gray-700">
                                                                {RO.title}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Vehicle Info */}
                                                    {RO?.vehicle && (
                                                        <div className="flex items-center space-x-2 mb-2 group-hover:font-semibold">
                                                            <div className="flex items-center space-x-1 text-gray-600">
                                                                <span className="text-sm">
                                                                    {getFullVehicleName(RO?.vehicle)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Customer Info */}
                                                    <div className="flex items-center space-x-2 text-xs text-gray-500 group-hover:font-semibold">
                                                        <UserIcon className="h-3 w-3" />
                                                        <span>
                                                            <CustomerFullName
                                                                customer={RO?.customer}
                                                                customerId={RO?.customer?.id}
                                                            />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                {(!activeRepairOrders || activeRepairOrders.length === 0) && (
                                    <div className="px-3 py-6 text-center text-gray-500">
                                        <Wrench className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                                        <p className="text-sm">No active repair orders</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Clock In/Out Button */}
            <div className="px-6 py-4">
                <button
                    onClick={() => {
                        setIsClockedIn(!isClockedIn);
                        if (!isClockedIn) {
                            startClock();
                        } else {
                            setStartTime(null);
                            stopClock();
                        }
                    }}
                    className={`w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                        isClockedIn
                            ? 'bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500'
                            : 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500'
                    }`}
                >
                    {isClockedIn ? (
                        <>
                            <Square className="h-4 w-4" />
                            <span>Clock Out</span>
                        </>
                    ) : (
                        <>
                            <Play className="h-4 w-4" />
                            <span>Clock In</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default UserClockInCard;
